import {
  Component,
  Prop,
  h,
  Element,
  Host,
  Listen,
  State,
} from "@stencil/core";
import { isAnonymous, mapWishlistEntry } from "./f-wishlist-service";
import state, { getWishlists } from "@stores/wishlist.store";
import {
  additionalSpacesHeight,
  singleItemHeight,
} from "../../f-flyout/f-flyout-expander/f-flyout-expander-helpers";
import { AddToWishlistEventDetails } from "../../../../components/wishlist/bcp-add-to-wishlist/bcp-add-to-wishlist-model";

@Component({
  tag: "f-wishlist-flyout",
  shadow: true,
})
export class WishlistFlyout {
  @Element() el: HTMLFWishlistFlyoutElement;

  @Prop() buttonHiddenLabel = "Wishlists";
  @Prop() buttonTitle = this.buttonHiddenLabel;
  @Prop() closeButtonHiddenLabel;
  @Prop() expanderButtonHiddenLabel;
  @Prop() flyoutTitle = "My wishlists";
  @Prop() wishlistNamePrefix = "Wishlist";
  @Prop() quantityLabel = "Quantity: ";
  @Prop() totalPriceLabel = "Total net price";
  @Prop() wishlistDetailUrl: string;
  @Prop() wishlistDetailButtonLabel = "Open wishlist";
  @Prop() emptyListLabel = "Your wishlist list is empty";
  @Prop() errorMessageLabel = "An error occurred";
  @Prop() allWishlistsUrl: string;
  @Prop() allWishlistsButtonLabel = "Detailed list of wishlists";
  @Prop() numberOfShownProducts = 3;

  /** Specifies the starting number of items on the flyout list. */
  @Prop({ mutable: true }) badgeCounter: number;

  @State() recentlyModifiedWishlist = "";

  @Listen("productAddedToWishlist", { target: "document" })
  async handleProductAddToWishlist(
    event: CustomEvent<AddToWishlistEventDetails>
  ): Promise<void> {
    this.flyoutEl.openFlyout(false);
    this.recentlyModifiedWishlist = event.detail.wishlistCode;
  }

  componentWillLoad(): void {
    this.updateWishlists();
    state.numberOfWishlistsItems = this.badgeCounter;
  }

  private async updateWishlists(): Promise<void> {
    getWishlists();
  }

  private flyoutEl;

  render() {
    return (
      <Host data-test="mini-wishlist">
        <f-flyout-with-button
          flyoutTitle={`${this.flyoutTitle} ${
            state.numberOfWishlistsItems
              ? `(${state.numberOfWishlistsItems})`
              : ""
          }`}
          flyoutId="f-wishlist-flyout"
          ref={(el) => (this.flyoutEl = el)}
          buttonIconName="wishlist"
          buttonBadgeLabel={state.numberOfWishlistsItems}
          buttonTitle={this.buttonTitle}
          closeButtonHiddenLabel={this.closeButtonHiddenLabel}
          flyoutDataTest="wishlist-flyout"
        >
          <f-visually-hidden>{this.buttonHiddenLabel}</f-visually-hidden>
          <div slot="flyout-content">
            {state.is === "LOADED" &&
              (state.wishlists.length > 0 ? (
                state.wishlists?.map((wishlist, index) => (
                  <f-flyout-expander
                    open={wishlist.code === this.recentlyModifiedWishlist}
                    showActionButtons={false}
                    key={wishlist.code}
                    withoutSeparator={
                      isAnonymous() && index === state.wishlists.length - 1
                    }
                    contentMaxHeight={
                      wishlist.entries?.length * singleItemHeight +
                      additionalSpacesHeight
                    }
                  >
                    <span slot="flyout-expander-heading">{`${
                      this.wishlistNamePrefix
                    } ${wishlist.name} (${
                      wishlist.entries == null
                        ? 0
                        : wishlist.entries.reduce(
                            (count, a) => count + a.quantity,
                            0
                          )
                    })`}</span>
                    <f-flyout-product-list
                      slot="flyout-expander-content"
                      entries={wishlist.entries?.map(mapWishlistEntry)}
                      price={wishlist.totalPrice}
                      quantityLabel={this.quantityLabel}
                      openDetailsButtonLabel={this.wishlistDetailButtonLabel}
                      priceLabel={this.totalPriceLabel}
                      emptyListLabel={this.emptyListLabel}
                      detailsUrl={`${this.wishlistDetailUrl}/${wishlist.code}`}
                      styleIntegrated={true}
                      numberOfItems={this.numberOfShownProducts}
                      expanderButtonHiddenLabel={this.expanderButtonHiddenLabel}
                    />
                  </f-flyout-expander>
                ))
              ) : (
                <f-flyout-message messageLabel={this.emptyListLabel} />
              ))}
            {state.is === "LOADING" && <f-flyout-loader />}
            {state.is === "ERROR" && (
              <f-flyout-message messageLabel={this.errorMessageLabel} />
            )}
          </div>
          {!isAnonymous() ? (
            <f-flyout-footer
              slot="flyout-footer"
              mode="secondary"
              link={this.allWishlistsUrl}
              label={this.allWishlistsButtonLabel}
            />
          ) : (
            ""
          )}
        </f-flyout-with-button>
      </Host>
    );
  }
}
